import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Tooltip, Button, Menu, MenuItem } from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import {
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import CircularLoading from '../CircularLoading';
import PropTypes from 'prop-types';
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { PrimaryButton, SecondaryButton } from "../../Common";

const exportType = {
    pdf: "pdf",
    csv: "csv",
    xlsm: "xlsm",
    xlsx: "xlsx"
};

const getMimeType = (type) => {
    switch (type) {
        case exportType.pdf:
            return "application/pdf";
        case exportType.xlsm:
            return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        default:
            return "text/csv";
    }
};

const getFileName = (type, customFileName) => {
    switch (type) {
        case exportType.pdf:
            return customFileName ? `${customFileName}_${Date.now()}.pdf` : `ServiceEvents_${Date.now()}.pdf`;
        case exportType.xlsm:
            return `${customFileName}.xlsm`;
        default:
            return `${customFileName}.csv`;
    }
};

/*
 * The component below is used only for
 * DataGrid & DataGridWithExpansionPanel
*/

const ToolBar = ({
    exportButtonTooltip,
    exportFileTypes,
    exportOptions,
    hideExportButton,
    hideFilterButton,
    enableExport,
    renderEditButton,
    editOnClick,
    cancelOnClick,
    editActive,
    saveDisabled,
    setEditActive,
    toolbarLeftContent
}) => {
    const [exportLoading, setExportLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleExport = async (fileType) => {
        const {
            exportURL,
            rowCount = 10,
            page = 1,
            sort,
            filters,
            type,
            exportFileName
        } = exportOptions;

        if (!exportURL) return toast.error("Could not fetch export data!", { duration: 5000 });
        setAnchorEl(null);
        setExportLoading(true);

        const response = await fetch(exportURL, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                rowCount,
                page,
                sortBy: sort,
                filters,
                exportType: fileType,
            }),
            responseType: 'blob'
        }).catch(err => {
            setError(err.message);
        });

        const data = await response.blob();

        console.log(response);

        if (response.status === 599) {
            toast.error("Export list too large.  Reduce size to less than 1000.", { duration: 5000 });
            return setExportLoading(false);
        }

        const customFileName = exportFileName || Date.now();
        const url = window.URL.createObjectURL(data, { type: getMimeType(fileType) });
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
            'download',
            getFileName(fileType, customFileName)
        );
        document.body.appendChild(link);
        link.click();
        return setExportLoading(false);
    };
    
    const cancelEdit = () => {
        cancelOnClick();
        return setEditActive(false);
    };

    return (
        <GridToolbarContainer sx={{ width: "100%" }}>
            {toolbarLeftContent}
            <GridToolbarColumnsButton />
            {!hideFilterButton && <GridToolbarFilterButton />}
            {!hideExportButton && (
                exportFileTypes.length === 1 ? (

                    <Tooltip
                        title={exportButtonTooltip}
                    >
                        <Button
                            size='medium'
                            startIcon={exportLoading
                                ? <CircularLoading marginLeft={1} size={15} />
                                : <FileUploadOutlinedIcon />
                            }
                            onClick={() => enableExport && handleExport('.csv')}
                            disabled={exportLoading}
                            sx={{ fontSize: "13px" , display: { xs: 'none', lg: 'inline-flex' }}}
                        >
                            {exportLoading ? "Exporting..." : "Export"}
                        </Button>
                    </Tooltip>
                ) : (
                    <>
                        <Tooltip title={'Multiple Export Options'}>
                            <Button
                                startIcon={exportLoading
                                    ? <CircularLoading marginLeft={1} size={15} />
                                    : <FileUploadOutlinedIcon />
                                }
                                onClick={(e) => enableExport && setAnchorEl(e.currentTarget)}
                                disabled={exportLoading}
                                sx={{ fontSize: "13px" }}
                            >
                                {exportLoading ? "Exporting..." : "Export"}
                            </Button>
                        </Tooltip>
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={() => setAnchorEl(null)}
                        >
                            {exportFileTypes.map((ft) => (
                                <MenuItem
                                    onClick={() => handleExport(ft.type)}
                                >
                                    {`Export as ${ft.name} (.${ft.type})`}
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                )
            )}
            {renderEditButton && (
                <div style={{ display: "flex", marginLeft: "auto", justifyContent: "space-between" }}>
                    {!editActive && (
                        <SecondaryButton
                            variant={"outlined"}
                            buttonText={"Edit"}
                            onClick={() => setEditActive(true)}
                            startIcon={<ModeEditIcon />}
                        />
                    )}
                    {editActive && (
                        <>
                            <PrimaryButton
                                buttonText={"Save"}
                                onClick={editOnClick}
                                disabled={saveDisabled}
                            />

                            <SecondaryButton
                                buttonText={"Cancel"}
                                onClick={cancelEdit}
                                sx={{ marginLeft: "3%" }}
                            />
                        </>
                    )}
                </div>
            )}
        </GridToolbarContainer>
    );
};

ToolBar.propTypes = {
    exportButtonTooltip: PropTypes.string,
    exportOptions: PropTypes.object,
    hideExportButton: PropTypes.bool,
    enableExport: PropTypes.bool,
    renderEditButton: PropTypes.bool,
    editOnClick: PropTypes.func,
    cancelOnClick: PropTypes.func,
    editActive: PropTypes.bool,
    saveDisabled: PropTypes.bool,
    setEditActive: PropTypes.func,
};

ToolBar.defaultProps = {
    exportButtonTooltip: "Export Data",
    exportOptions: {},
    hideExportButton: false,
    enableExport: true,
    renderEditButton: false,
    editOnClick: () => {},
    cancelOnClick: () => {},
    editActive: false,
    saveDisabled: false,
    setEditActive: () => {},
};

export default ToolBar;
